import { ProductTypeEnum, type MarketAccount, type Security, type UnifiedLiquidityResponse } from '@talos/kyoko';
import type { MarketSelectorUnifiedLiquiditySelection } from '../types';

export function isValidUnifiedLiquidityTokenSecurity(
  security: Security | undefined
): security is Security & { QuoteCurrency: string } {
  if (!security?.QuoteCurrency) {
    return false;
  }

  switch (security.ProductType) {
    case ProductTypeEnum.Spot:
    case ProductTypeEnum.PerpetualSwap:
    case ProductTypeEnum.CFD:
    case ProductTypeEnum.Future:
    case ProductTypeEnum.Basis:
    case ProductTypeEnum.Option:
    case ProductTypeEnum.CalendarSpread:
    case ProductTypeEnum.Synthetic:
      return true;
    default:
      return false;
  }
}

export function getUnifiedLiquiditySelectionsForMarketAccounts({
  symbol,
  selectedMarketAccounts,
  marketAccountsByName,
  securitiesBySymbol,
  unifiedLiquidityTokenBySymbol,
}: {
  symbol: string | undefined;
  selectedMarketAccounts: string[];
  marketAccountsByName: Map<string, MarketAccount>;
  securitiesBySymbol: Map<string, Security>;
  unifiedLiquidityTokenBySymbol: Map<string, UnifiedLiquidityResponse> | undefined;
}): MarketSelectorUnifiedLiquiditySelection[] {
  const primarySecurity = symbol ? securitiesBySymbol.get(symbol) : undefined;
  const unifiedLiquidityTokens = symbol ? unifiedLiquidityTokenBySymbol?.get(symbol)?.Tokens : undefined;
  if (!symbol || !primarySecurity || !unifiedLiquidityTokens?.length) {
    return [];
  }

  const selectionsByKey = new Map<string, MarketSelectorUnifiedLiquiditySelection>();

  for (const marketAccountName of selectedMarketAccounts) {
    const marketAccount = marketAccountsByName.get(marketAccountName);
    if (!marketAccount) {
      continue;
    }
    const primarySymbols = primarySecurity.Markets?.includes(marketAccount.Market) ? [symbol] : [];
    const tokenSymbols = unifiedLiquidityTokens
      .filter(token => token.Market === marketAccount.Market)
      .map(token => token.Symbol)
      .filter(tokenSymbol => isValidUnifiedLiquidityTokenSecurity(securitiesBySymbol.get(tokenSymbol)));

    for (const selectionSymbol of [...primarySymbols, ...tokenSymbols]) {
      selectionsByKey.set(`${marketAccountName}|${selectionSymbol}`, {
        marketAccount: marketAccountName,
        symbol: selectionSymbol,
      });
    }
  }

  return Array.from(selectionsByKey.values());
}

export function getSelectedMarketAccountsWithoutUnifiedLiquiditySymbols({
  selectedMarketAccounts,
  marketSymbols,
}: {
  selectedMarketAccounts: string[];
  marketSymbols: MarketSelectorUnifiedLiquiditySelection[];
}) {
  const marketAccountsWithSymbols = new Set(marketSymbols.map(selection => selection.marketAccount));
  return selectedMarketAccounts.filter(marketAccount => !marketAccountsWithSymbols.has(marketAccount));
}

export function getSelectedMarketAccountsWithUnifiedLiquiditySymbols({
  selectedMarketAccounts,
  marketSymbols,
}: {
  selectedMarketAccounts: Iterable<string>;
  marketSymbols: MarketSelectorUnifiedLiquiditySelection[];
}) {
  const selectedMarketAccountSet = new Set(selectedMarketAccounts);
  return Array.from(
    new Set(
      marketSymbols
        .filter(selection => selectedMarketAccountSet.has(selection.marketAccount))
        .map(selection => selection.marketAccount)
    )
  );
}

export type UnifiedLiquiditySelection = {
  marketAccount: string;
  symbol: string;
};

export function getUnifiedLiquiditySelectionKey(selection: UnifiedLiquiditySelection) {
  return `${selection.marketAccount}|${selection.symbol}`;
}

export function getUnifiedLiquiditySelectionsForEnable<TSelection extends UnifiedLiquiditySelection>({
  configuredSelections,
  fallbackSelections,
}: {
  configuredSelections: TSelection[];
  fallbackSelections: TSelection[];
}) {
  return configuredSelections.length ? configuredSelections : fallbackSelections;
}

export function hasUnifiedLiquiditySelection<TSelection extends UnifiedLiquiditySelection>(
  selections: TSelection[],
  selection: UnifiedLiquiditySelection
) {
  return selections.some(
    selected => selected.marketAccount === selection.marketAccount && selected.symbol === selection.symbol
  );
}

export function toggleUnifiedLiquiditySelections<TSelection extends UnifiedLiquiditySelection>(
  selectedSelections: TSelection[],
  targetSelections: TSelection[],
  selected: boolean
) {
  if (selected) {
    return [...selectedSelections, ...targetSelections].filter(
      (selection, index, selections) =>
        selections.findIndex(
          other => getUnifiedLiquiditySelectionKey(other) === getUnifiedLiquiditySelectionKey(selection)
        ) === index
    );
  }

  return selectedSelections.filter(
    selectedSelection =>
      !targetSelections.some(
        targetSelection =>
          targetSelection.marketAccount === selectedSelection.marketAccount &&
          targetSelection.symbol === selectedSelection.symbol
      )
  );
}
